import React, { useEffect, useReducer, useRef, useState } from 'react'

import { CSSTransition } from 'react-transition-group'
import { useTranslation } from 'i18n'

import { scrollToTopOfCurrentStep } from 'services/break'
import { sleep } from 'util/misc'

import ChecklistPanel from './components/ChecklistPanel'
import StepHeader from './components/StepHeader'
import ResultPanel from './components/ResultPanel'
import LoadingPanel from './components/LoadingPanel'
import analytics from 'global/AnalyticsData'

import './TipsAndTricks.scss'

const ActionType = {
  INIT: 'INIT',
  UPDATE: 'UPDATE',
  CALCULATED_HEIGHT: 'CALCULATED_HEIGHT',
  DONE: 'DONE',
}

const reducer = (state, action) => {
  switch (action.type) {
    case ActionType.INIT:
      return {
        ...initialState,
      }
    case ActionType.UPDATE:
      return {
        ...state,
        text: state.text + action.text,
      }

    case ActionType.DONE:
      return {
        ...state,
        Complete: true,
      }
    case ActionType.CALCULATED_HEIGHT:
      return {
        ...state,
        fixedHeightForMasonryEffect: action.fixedHeightForMasonryEffect,
      }
    default:
      console.warn(`unhandled action.type: ${action.type}`)
  }
}

const initialState = {
  text: '',
  Complete: false,
  fixedHeightForMasonryEffect: 400,
}

const TipsAndTricks = ({
  brk,
  step,
  onEngage = function () {},
  onAwardPoints = function () {},
  nextStepButton,
  skipButton,
  service,
}) => {
  const { t } = useTranslation(['tipsAndTricks', 'common'])
  const [state, dispatch] = useReducer(reducer, initialState)
  const [showResult, setShowResult] = useState(false)
  const [showError, setShowError] = useState(false)
  const [recipeResults, setRecipeResults] = useState([])
  const [checkedItemsList, setCheckedItemsList] = useState([])

  const [formItemsChecked, setFormItemsChecked] = useState({})

  const checklistPanelArrayRef = useRef([])

  const tipsAndTricks = step && step.gen_ai_tips

  const animationDuration = 300
  const setInitialCheckedItemsList = () => {
    const initialCheckedItemsList = []
    step.gen_ai_tips.checklists.forEach(checklist => {
      if (checklist.selectionMode === 'ONE') {
        initialCheckedItemsList[checklist.genAIPromptText] = [
          checklist.items[0].genAIPromptText,
        ]
      }
    })
    setCheckedItemsList(initialCheckedItemsList)
  }
  useEffect(() => {
    setInitialCheckedItemsList()
  }, [])

  // oof. to get a flexbox masonry effect to work we need a fixed height for the container.
  // to calculate a height we need to wait until content is rendered (useEffect) but also
  // after fonts have loaded.
  useEffect(() => {
    dispatch({
      type: ActionType.CALCULATED_HEIGHT,
      fixedHeightForMasonryEffect: getMaxHeight(),
    })

    // recalculate every 500 ms to account for fonts and images loading
    const timerId = setInterval(() => {
      const measuredHeight = getMaxHeight()
      if (measuredHeight !== state.fixedHeightForMasonryEffect) {
        dispatch({
          type: ActionType.CALCULATED_HEIGHT,
          fixedHeightForMasonryEffect: measuredHeight,
        })
      }
    }, 500)

    return () => clearInterval(timerId)
  }, [state.fixedHeightForMasonryEffect])

  const getPropmt = () => {
    try {
      var header =
        tipsAndTricks.genAIPrompt +
        ' in pure json, with "title", "what_to_do", "why_it_works" and "what_you_need"(in string, no ordinal numbers).'
      var body = Object.keys(checkedItemsList).map(item =>
        checkedItemsList[item].length > 0
          ? ' ' +
            item +
            ': ' +
            checkedItemsList[item].reduce((prev, cur) => prev + ', ' + cur) +
            '.'
          : '',
      )
      var tail = ` Return 1 object that contains an array of those results.`
      return (
        header +
        (body.length > 0 ? body.reduce((prev, cur) => prev + cur) : '') +
        (tipsAndTricks.genAIResultNumber !== 0
          ? ` Make sure to only generate ${tipsAndTricks.genAIResultNumber} results.`
          : '') +
        tail
      )
    } catch (e) {
      console.error(e)
      return ''
    }
  }

  // Chat GPT's response can be in different format
  // 1. {Here are ... 1.name json{...}, ...}
  // 2. {tips: {...}}
  // 3. {...}
  const formatResponse = response => {
    var res = []
    // Situation 1
    if (response[0] !== '{') {
      var newRes = ''
      var shouldAdd = false
      for (let i = 0; i < response.length; i++) {
        if (response[i] === '{') {
          shouldAdd = true
        } else if (response[i] === '}') {
          shouldAdd = false
          newRes += '}'
          res.push(JSON.parse(newRes))
          newRes = ''
        }
        if (shouldAdd) {
          newRes += response[i]
        }
      }
      return res[0]
    }
    res = JSON.parse(response)
    // Situation 2
    if (Object.keys(res).length === 1) {
      res = res[Object.keys(res)[0]]
      return res
    }
    // Situation 3
    if (Object.keys(res).length >= 4) {
      return res
    }

    //Invalid response
    console.warn('Unexpected format', response)
    return res
  }

  // somtimes instructions are formatted to include numbers in front of each item, sometimes they don't.
  // this removes the numbers in front of each item
  // e.g. "2. Add flour" becomes "Add flour"
  const formatInstructions = recipe => {
    if (recipe && recipe.instructions && recipe.instructions.length > 0) {
      recipe.instructions = recipe.instructions.map(item => {
        // regex help for (^[\d]+\. )
        // ^ asserts position at start of a line
        // [\d]+ matches one or more digits
        // \. matches the character '.'
        // there's a space character after the \. that matches a literal single space character
        // the parans () group all that
        // paste it here to play around: https://regex101.com/
        return item.replace(/(^[\d]+\. )/, '')
      })
    }
    return recipe
  }

  const handleSubmit = async () => {
    var startTime, endTime
    startTime = new Date()
    scrollToTopOfCurrentStep(step.id)
    await sleep(animationDuration)

    analytics.stepEvent('step_interaction_genai_submit', step)

    setShowResult(true)

    var prompt

    try {
      prompt = getPropmt()
      dispatch({
        type: ActionType.INIT,
      })
      const response = await service.post(`request`, {
        permalink: brk.permalink,
        prompt: prompt,
        stepType: step.type,
        language: tipsAndTricks.contentLanguage,
      })
      endTime = new Date()
      analytics.stepEvent('step_interaction_genai_response', step, {
        context_position: endTime - startTime,
      })

      if (response.status !== 200) {
        throw new Error(response.status)
      }

      // sometimes response.body is null.
      // this may be on a slow connection or slow machine?
      // or this may be due to some piece of infra causing an issue (Cloudfront)
      // cause unknown. this is a band-aid.
      if (response.body) {
        const reader = response.body.getReader()
        let done = false
        let value
        let result = ''
        let count = 0

        while (!done) {
          ;({ value, done } = await reader.read())
          let token = new TextDecoder().decode(value) // value is Uint8Array
          result = result + token

          for (let c of token) {
            if (c === '{') {
              count += 1
            } else if (c === '}') {
              count -= 1
              if (count === 0) {
                done = true
              }
            }
          }

          token = token
            ? token
                .replaceAll('\n', '<br/>')
                // Remove special chars (like Unicode block)
                .replace(/[^\x00-\x7F]/g, '')
                // Remove special chars
                .replace(/[`,'"\{\}\[\]\\\/]/gi, '')
            : ''
          dispatch({
            type: ActionType.UPDATE,
            text: token,
          })
        }

        // Need this in case of parsing error
        recipeResults.push(
          formatInstructions(
            formatResponse(result.replace(/[^\x00-\x7F]/g, '')),
          ),
        )
        setRecipeResults(recipeResults)
      } else {
        console.log(`response.body is null - we cannot stream`)
        const body = await response.text()

        recipeResults.push(
          formatInstructions(formatResponse(body.replace(/[^\x00-\x7F]/g, ''))),
        )
        setRecipeResults(recipeResults)
      }

      dispatch({
        type: ActionType.DONE,
      })
    } catch (error) {
      console.log(error)
      setShowError(true)
    }

    onEngage()
    onAwardPoints()
  }

  // checked, selectedItem, type, checklistItemId
  const handleClickItem = (
    checked,
    selectedItem,
    type = 'CHECKBOX',
    checklistItemId,
  ) => {
    if (type !== 'CHECKBOX') {
      // For radio buttons, clear previous selections in the same group
      setCheckedItemsList(prev => ({
        ...prev,
        [checklistItemId]: [selectedItem.genAIPromptText],
      }))
    } else {
      // For checkboxes, toggle the selection
      setCheckedItemsList(prev => {
        const updatedList = { ...prev }
        if (checked) {
          if (!updatedList[checklistItemId]) {
            updatedList[checklistItemId] = []
          }
          updatedList[checklistItemId].push(selectedItem.genAIPromptText)
        } else {
          updatedList[checklistItemId] = updatedList[checklistItemId].filter(
            item => item !== selectedItem.genAIPromptText,
          )
        }
        console.log('updatedList', updatedList)
        return updatedList
      })
    }

    if (checked || type !== 'CHECKBOX') {
      var checkListCard = tipsAndTricks.checklists.filter(
        card =>
          card.genAIPromptText && card.genAIPromptText === checklistItemId,
      )[0]

      var cardItem = checkListCard.items.filter(
        item => item.id === selectedItem.id,
      )[0]
      const label = selectedItem.label

      analytics.stepEvent('step_interaction_genai_select', step, {
        result_id: cardItem.id,
        result: label,
        context_id: checkListCard.id,
        context: checkListCard.title,
        context_total: checkListCard.items.length,
      })
    }
  }

  const isChecked = (label, title) => {
    return (
      checkedItemsList[checklistItemId] &&
      checkedItemsList[checklistItemId].indexOf(label) > -1
    )
  }

  const getMaxHeight = () => {
    let leftHeight = 0
    let rightHeight = 0
    const PADDING = 20 // this isn't exact, but close. Is there a property better than clientHeight (below)
    checklistPanelArrayRef.current.forEach((checklistRef, index) => {
      if (index % 2 === 0) {
        leftHeight += (checklistRef ? checklistRef.clientHeight : 0) + PADDING
      } else {
        rightHeight += (checklistRef ? checklistRef.clientHeight : 0) + PADDING
      }
    })

    return Math.max(leftHeight, rightHeight)
  }

  // Give randomness to images in image library
  const shuffle = array => {
    let currentIndex = array.length

    // While there remain elements to shuffle...
    while (currentIndex !== 0) {
      // Pick a remaining element...
      let randomIndex = Math.floor(Math.random() * currentIndex)
      currentIndex--

      // And swap it with the current element.
      ;[array[currentIndex], array[randomIndex]] = [
        array[randomIndex],
        array[currentIndex],
      ]
    }

    return array
  }

  return (
    <section className="tipsAndTricks-step3">
      <div
        className={`step-content ${
          brk?.navigationType === 'TOP' ? 'top-navigation' : ''
        } ${step.backgroundImage ? ' with-shadow' : ''}`}
      >
        <CSSTransition
          in={true}
          timeout={animationDuration}
          classNames="exit-fadeout"
          unmountOnExit={true}
          onExit={() => {
            // with long lists we need to make sure we get back to the top
            scrollToTopOfCurrentStep(step.id)
          }}
          onExiting={() => {
            scrollToTopOfCurrentStep(step.id)
          }}
          onExited={() => {
            scrollToTopOfCurrentStep(step.id)
          }}
        >
          <>
            {!showResult ? (
              <div>
                {/* need a wrapper div for CSSTransition classes */}
                <StepHeader
                  title={tipsAndTricks.title}
                  copy={tipsAndTricks.copy}
                  imageId={tipsAndTricks.headerImageId}
                  alt={``}
                />
                <div
                  className="checklist-panel"
                  style={{ maxHeight: state.fixedHeightForMasonryEffect }}
                >
                  {tipsAndTricks &&
                    tipsAndTricks.checklists &&
                    tipsAndTricks.checklists.map((checklist, i) => {
                      const {
                        id,
                        title,
                        genAIPromptText,
                        copy,
                        imageType,
                        imageId,
                        items,
                        selectionMode,
                      } = checklist
                      return (
                        <ChecklistPanel
                          key={i}
                          ref={el => (checklistPanelArrayRef.current[i] = el)}
                          id={id}
                          title={title}
                          titlePrompt={genAIPromptText || title}
                          detail={copy}
                          imageType={imageType}
                          mode={selectionMode}
                          imageUrl={imageId}
                          imageAlt={imageId}
                          items={items}
                          disabled={false}
                          onClick={handleClickItem}
                          setState={setFormItemsChecked}
                          isChecked={isChecked}
                        />
                      )
                    })}
                </div>
                <div className="submit-container">
                  <input
                    type="submit"
                    className="btn themed-button"
                    disabled={false}
                    value={t('common:submit')}
                    onClick={handleSubmit}
                  />
                </div>
              </div>
            ) : (
              <div className="step-content with-shadow tips-and-tricks-result">
                <StepHeader
                  title={tipsAndTricks.resultTitle}
                  copy={tipsAndTricks.resultCopy}
                  imageId={tipsAndTricks.resultImageId}
                  alt={``}
                />
                <div className="result-panel">
                  {
                    <ResultPanel
                      resultCopyAllToClipboard={
                        tipsAndTricks.resultCopyAllToClipboard
                      }
                      result={recipeResults[0]}
                      state={state}
                      step={tipsAndTricks}
                      atc={tipsAndTricks.atc}
                      globalAtc={tipsAndTricks.globalAtc}
                      imageLibrary={shuffle(tipsAndTricks.imageLibrary)}
                    />
                  }
                </div>
              </div>
            )}
            {showError && (
              <div className="error-message">
                {t('tipsAndTricks:error.generation')}
                <div className=" error-btn">
                  <span
                    className="btn themed-button"
                    onClick={() => {
                      setShowError(false)
                      setShowResult(false)
                    }}
                  >
                    {t('common:retry')}
                  </span>
                </div>
              </div>
            )}
          </>
        </CSSTransition>
        {skipButton}
        {nextStepButton}
      </div>
    </section>
  )
}

export default TipsAndTricks
